import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import { Box } from "@mui/material";
import { type FC, useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAnalytics } from "@/contexts/analytics/hooks";
import { useHospitalData } from "@/contexts/HospitalDataProvider";
import { useUpdateShiftNotes } from "@/hooks/useUpdateShiftNotes";
import { SheetActionRow } from "@/ui/Clickable/SheetActionRow";
import { SystemValidationTextField } from "@/ui/Inputs/SystemValidationTextField";
import SystemDialog from "@/ui/Popups/Dialog";
import { SheetStep } from "@/ui/Popups/SheetStep";
import { SheetStepButton } from "@/ui/Popups/SheetStepButton";
import type { NurseInShift } from "@/utils/models";
import { GapsStaffStep } from "./gapsSheetTypes";

type Props = {
	nurseInShift: NurseInShift;
	/** A punched shift on a past day: the note is the actual shift's. */
	isActual: boolean;
	name: string;
	/** The shift the note belongs to, e.g. "Nurse 7a-3p · Thu 1 Oct". */
	context: string;
	onStep: (step: GapsStaffStep) => void;
};

const SHEET = "gaps-staff-note";

/** Rows the note field opens at — enough for a sentence or two without the sheet looking like a form. */
const NOTE_MIN_ROWS = 3;

/**
 * The shift's note (P3-5), saved through `useUpdateShiftNotes` — the Daily Gaps note field's own write, local
 * update and snackbar. It saves on the button, never on Enter: the desktop field listens for Enter on the whole
 * window, which in a sheet would fire on any keyboard's return key, so the extraction left WHEN to save to the
 * caller. An unchanged note writes nothing.
 *
 * The footer is the step's way back and its save, whether the shift has a note or not. Deleting an existing note is
 * a quieter row under the field and asks first: it is saving the note empty, and a note is someone's words.
 */
export const GapsStaffNoteStep: FC<Props> = ({ nurseInShift, isActual, name, context, onStep }) => {
	const { t } = useTranslation();
	const { track, trackError } = useAnalytics();
	const { chosenDepartment } = useHospitalData();
	const { saveNotes, isSaving } = useUpdateShiftNotes(nurseInShift, isActual);
	// DEFAULT: a shift with no note edits from an empty field — the same reading `useUpdateShiftNotes` gives it.
	const savedNote = nurseInShift.notes ?? "";
	const [draft, setDraft] = useState<string>(savedNote);
	const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState<boolean>(false);
	const hasNote = savedNote !== "";

	const save = useCallback(
		async (notes: string): Promise<void> => {
			const properties = { department_id: chosenDepartment.department_id, shift_id: nurseInShift.id, cleared: notes === "" };
			track("gaps_staff_note_save_start", properties);
			try {
				await saveNotes(notes);
			} catch (error) {
				// The mutation's own error snackbar has told the user; the step stays so they can retry.
				trackError("gaps_staff_note_save_error", { ...properties, error_message: error instanceof Error ? error.message : String(error) });
				return;
			}
			track("gaps_staff_note_save_success", properties);
			onStep(GapsStaffStep.Actions);
		},
		[chosenDepartment.department_id, nurseInShift.id, onStep, saveNotes, track, trackError],
	);

	return (
		<>
			<SheetStep
				stepKey={GapsStaffStep.Note}
				dataTestId={SHEET}
				title={hasNote ? t("gaps.staffSheet.editNote") : t("gaps.staffSheet.addNote")}
				subtitle={`${name} · ${context}`}
				footer={
					<>
						<SheetStepButton tone="dismiss" onClick={() => onStep(GapsStaffStep.More)} disabled={isSaving} dataTestId={`${SHEET}-cancel`}>
							{t("back")}
						</SheetStepButton>
						<SheetStepButton
							tone="confirm"
							onClick={() => void save(draft.trim())}
							loading={isSaving}
							disabled={draft.trim() === savedNote.trim()}
							dataTestId={`${SHEET}-confirm`}
						>
							{t("gaps.staffSheet.saveNote")}
						</SheetStepButton>
					</>
				}
			>
				<Box sx={{ px: 2, py: 1.75 }}>
					<SystemValidationTextField
						value={draft}
						onChange={(event) => setDraft(event.target.value)}
						placeholder={t("gaps.staffSheet.notePlaceholder")}
						reserveHeightForHelperText={false}
						autoFocus
						fullWidth
						injectedProps={{ multiline: true, minRows: NOTE_MIN_ROWS }}
						dataTestId={`${SHEET}-input`}
					/>
				</Box>
				{hasNote && (
					<SheetActionRow
						tone="danger"
						icon={<DeleteOutlineOutlinedIcon />}
						label={t("gaps.staffSheet.deleteNote")}
						disabled={isSaving}
						onClick={() => setIsDeleteConfirmOpen(true)}
						dataTestId={`${SHEET}-delete`}
					/>
				)}
			</SheetStep>
			<SystemDialog
				dataTestId={`${SHEET}-delete-dialog`}
				open={isDeleteConfirmOpen}
				onClose={() => setIsDeleteConfirmOpen(false)}
				title={t("gaps.staffSheet.deleteNoteTitle", { name })}
				body={t("gaps.staffSheet.deleteNoteBody")}
				confirm={t("gaps.staffSheet.deleteNote")}
				confirmButtonVariant="delete"
				confirmLoading={isSaving}
				onConfirm={() => {
					setIsDeleteConfirmOpen(false);
					void save("");
				}}
			/>
		</>
	);
};
